import React, { useState } from "react";
import getMonthsInRange from "../Month";
import useCompanyStore from "../../../../store/useCompanyStore";
import { format } from "date-fns";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Download, FileText, Table } from 'lucide-react';

const CashFlowTable = ({ data, onAccountClick }) => {
  const [editing, setEditing] = useState(false);
  const [cashFlowTitle, setCashFlowTitle] = useState("Cash Flow Statement");
  const [showExportOptions, setShowExportOptions] = useState(false);
  const { companyInfo } = useCompanyStore();

  // Categorize accounts, excluding those with a netFlow of 0
  const categorizeAccounts = (accounts = [], category) => {
    return accounts.filter(
      (acc) => acc.category === category && acc.netFlow !== 0
    );
  };

  // Calculate total inflows, outflows, and net cash flow
  const calculateTotals = (accounts) => {
    const totalInflows = accounts.reduce((sum, acc) => sum + acc.inflows, 0);
    const totalOutflows = accounts.reduce((sum, acc) => sum + acc.outflows, 0);
    return {
      totalInflows,
      totalOutflows,
      netCashFlow: totalInflows - totalOutflows,
    };
  };

  // Export to PDF function
  const exportToPDF = async () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;

    // Add company logo if available
    if (companyInfo?.logo) {
      try {
        const logoSize = 30; // Increased logo size
        const logoX = pageWidth - margin - logoSize;
        const logoY = 10;
        doc.addImage(companyInfo.logo, 'PNG', logoX, logoY, logoSize, logoSize);
      } catch (error) {
        console.error('Error adding logo to PDF:', error);
      }
    }

    // Company Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    const companyName = companyInfo?.name || "NO COMPANY NAME";
    doc.text(companyName, pageWidth / 2, 25, { align: 'center' });

    // Report Title
    doc.setFontSize(14);
    doc.text(cashFlowTitle, pageWidth / 2, 35, { align: 'center' });

    // Date
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`As of: ${format(new Date(), "MMMM dd, yyyy")}`, pageWidth / 2, 42, { align: 'center' });

    // Prepare table data
    const tableData = [];
    const categories = ["Operating Activities", "Investing Activities", "Financing Activities"];

    categories.forEach(category => {
      // Category header
      tableData.push([
        { content: category.toUpperCase(), styles: { fontStyle: 'bold', fillColor: [59, 130, 246], textColor: [255, 255, 255] } },
        ...data.map(() => ''),
        ''
      ]);

      // Get accounts for this category
      const categoryAccounts = data[0]?.data ?
        categorizeAccounts(data[0].data, category)
          .filter(account => {
            const totalBalance = data
              .map(dataset => {
                const matchingAccount = dataset.data.find(acc => acc.accountName === account.accountName);
                return matchingAccount ? matchingAccount.netFlow : 0;
              })
              .reduce((sum, val) => sum + val, 0);
            return totalBalance !== 0;
          }) : [];

      // Account rows
      categoryAccounts.forEach(account => {
        const row = [
          '  ' + account.accountName,
          ...data.map(dataset => {
            const matchingAccount = dataset.data.find(acc => acc.accountName === account.accountName);
            return matchingAccount ? matchingAccount.netFlow.toLocaleString() + ' Nu.' : '0 Nu.';
          }),
          // Net cash flow for account
          data
            .map(dataset => {
              const matchingAccount = dataset.data.find(acc => acc.accountName === account.accountName);
              return matchingAccount ? matchingAccount.netFlow : 0;
            })
            .reduce((sum, val) => sum + val, 0)
            .toLocaleString() + ' Nu.'
        ];
        tableData.push(row);
      });

      // Category total row
      const totalRow = [
        { content: `Total ${category}`, styles: { fontStyle: 'bold' } },
        ...data.map(dataset => {
          const accounts = categorizeAccounts(dataset.data, category);
          const { netCashFlow } = calculateTotals(accounts);
          return { content: netCashFlow.toLocaleString() + ' Nu.', styles: { fontStyle: 'bold' } };
        }),
        {
          content: data
            .map(dataset => {
              const accounts = categorizeAccounts(dataset.data, category);
              const { netCashFlow } = calculateTotals(accounts);
              return netCashFlow;
            })
            .reduce((sum, val) => sum + val, 0)
            .toLocaleString() + ' Nu.',
          styles: { fontStyle: 'bold', textColor: [59, 130, 246] }
        }
      ];
      tableData.push(totalRow);
    });

    // Overall Net Cash Flow row
    const overallNetCashFlowTotal = data
      .map(dataset => {
        return categories
          .map(category => {
            const accounts = categorizeAccounts(dataset.data, category);
            const { netCashFlow } = calculateTotals(accounts);
            return netCashFlow;
          })
          .reduce((sum, val) => sum + val, 0);
      })
      .reduce((sum, val) => sum + val, 0);

    const overallNetRow = [
      { content: 'NET CASH FLOW', styles: { fontStyle: 'bold', fillColor: [59, 130, 246], textColor: [255, 255, 255] } },
      ...data.map(dataset => {
        const totalNetCashFlow = categories
          .map(category => {
            const accounts = categorizeAccounts(dataset.data, category);
            const { netCashFlow } = calculateTotals(accounts);
            return netCashFlow;
          })
          .reduce((sum, val) => sum + val, 0);
        return {
          content: totalNetCashFlow.toLocaleString() + ' Nu.',
          styles: { fontStyle: 'bold', fillColor: [59, 130, 246], textColor: [255, 255, 255] }
        };
      }),
      {
        content: overallNetCashFlowTotal.toLocaleString() + ' Nu.',
        styles: { fontStyle: 'bold', fillColor: [59, 130, 246], textColor: [255, 255, 255] }
      }
    ];
    tableData.push(overallNetRow);

    // Create table
    doc.autoTable({
      head: [['Account Name', ...data.map(dataset => getMonthsInRange(dataset.date)), 'Net Cash Flow']],
      body: tableData,
      startY: 50,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { halign: 'left' },
        ...Object.fromEntries(
          Array.from({ length: data.length + 1 }, (_, i) => [i + 1, { halign: 'right' }])
        )
      },
      didDrawPage: function (data) {
        // Footer
        const pageNumber = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(`Page ${data.pageNumber} of ${pageNumber}`, pageWidth - margin, doc.internal.pageSize.height - 10, { align: 'right' });
      }
    });

    doc.save(`${cashFlowTitle.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    setShowExportOptions(false);
  };

  // Export to CSV function
  const exportToCSV = () => {
    let csvContent = '';

    // Header
    csvContent += `${companyInfo?.name || "NO COMPANY NAME"}\n`;
    csvContent += `${cashFlowTitle}\n`;
    csvContent += `As of: ${format(new Date(), "MMMM dd, yyyy")}\n\n`;

    // Table headers
    const headers = ['Account Name', ...data.map(dataset => getMonthsInRange(dataset.date)), 'Net Cash Flow'];
    csvContent += headers.join(',') + '\n';

    // Process each category
    const categories = ["Operating Activities", "Investing Activities", "Financing Activities"];

    categories.forEach(category => {
      // Category header
      csvContent += category.toUpperCase() + ','.repeat(data.length + 1) + '\n';

      // Get accounts for this category
      const categoryAccounts = data[0]?.data ?
        categorizeAccounts(data[0].data, category)
          .filter(account => {
            const totalBalance = data
              .map(dataset => {
                const matchingAccount = dataset.data.find(acc => acc.accountName === account.accountName);
                return matchingAccount ? matchingAccount.netFlow : 0;
              })
              .reduce((sum, val) => sum + val, 0);
            return totalBalance !== 0;
          }) : [];

      // Account rows
      categoryAccounts.forEach(account => {
        const row = [
          account.accountName,
          ...data.map(dataset => {
            const matchingAccount = dataset.data.find(acc => acc.accountName === account.accountName);
            return matchingAccount ? matchingAccount.netFlow.toLocaleString() + ' Nu.' : '0 Nu.';
          }),
          // Net cash flow for account
          data
            .map(dataset => {
              const matchingAccount = dataset.data.find(acc => acc.accountName === account.accountName);
              return matchingAccount ? matchingAccount.netFlow : 0;
            })
            .reduce((sum, val) => sum + val, 0)
            .toLocaleString() + ' Nu.'
        ];
        csvContent += row.join(',') + '\n';
      });

      // Category total row
      const totalRow = [
        `Total ${category}`,
        ...data.map(dataset => {
          const accounts = categorizeAccounts(dataset.data, category);
          const { netCashFlow } = calculateTotals(accounts);
          return netCashFlow.toLocaleString() + ' Nu.';
        }),
        data
          .map(dataset => {
            const accounts = categorizeAccounts(dataset.data, category);
            const { netCashFlow } = calculateTotals(accounts);
            return netCashFlow;
          })
          .reduce((sum, val) => sum + val, 0)
          .toLocaleString() + ' Nu.'
      ];
      csvContent += totalRow.join(',') + '\n';
    });

    // Overall Net Cash Flow row
    csvContent += '\n';
    const overallNetRow = [
      'NET CASH FLOW',
      ...data.map(dataset => {
        const totalNetCashFlow = categories
          .map(category => {
            const accounts = categorizeAccounts(dataset.data, category);
            const { netCashFlow } = calculateTotals(accounts);
            return netCashFlow;
          })
          .reduce((sum, val) => sum + val, 0);
        return totalNetCashFlow.toLocaleString() + ' Nu.';
      }),
      data
        .map(dataset => {
          return categories
            .map(category => {
              const accounts = categorizeAccounts(dataset.data, category);
              const { netCashFlow } = calculateTotals(accounts);
              return netCashFlow;
            })
            .reduce((sum, val) => sum + val, 0);
        })
        .reduce((sum, val) => sum + val, 0)
        .toLocaleString() + ' Nu.'
    ];
    csvContent += overallNetRow.join(',') + '\n';

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${cashFlowTitle.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportOptions(false);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm mt-4">
      <div className="mb-8 flex flex-col items-center justify-center">
        {companyInfo?.logo && (
          <img
            src={companyInfo.logo}
            alt="Company Logo"
            className="h-24 w-auto object-contain mb-4"
          />
        )}

        <h1 className="text-3xl font-extrabold text-gray-900 mb-2 text-center">
          {companyInfo ? companyInfo.name : "NO COMPANY NAME"}
        </h1>

        <div className="flex justify-center mb-2">
          <h2
            className="text-xl font-bold text-gray-700 cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => setEditing(true)}
          >
            {editing ? (
              <input
                type="text"
                value={cashFlowTitle}
                onChange={(e) => setCashFlowTitle(e.target.value)}
                onBlur={() => setEditing(false)}
                autoFocus
                className="border-b-2 border-gray-300 focus:outline-none focus:border-blue-500 text-center bg-transparent"
              />
            ) : (
              `${cashFlowTitle} as of ${new Date().toLocaleDateString()}`
            )}
          </h2>
        </div>

        {/* Export Button */}
        <div className="relative mt-4 flex justify-center">
          <button
            onClick={() => setShowExportOptions(!showExportOptions)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors duration-200 font-medium"
          >
            <Download size={18} />
            Export Report
          </button>

          {/* Export Options Dropdown */}
          {showExportOptions && (
            <div className="absolute top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10 min-w-[180px]">
              <button
                onClick={exportToPDF}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors duration-200"
              >
                <FileText size={16} className="text-blue-500" />
                <span>Export as PDF</span>
              </button>
              <button
                onClick={exportToCSV}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors duration-200"
              >
                <Table size={16} className="text-blue-600" />
                <span>Export as CSV</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="text-gray-700 font-semibold border-b">
              <th className="px-4 py-3 text-left">Account Name</th>
              {data.map((dataset, index) => (
                <th key={index} className="px-4 py-3 text-right">
                  {getMonthsInRange(dataset.date)}
                </th>
              ))}
              <th className="px-4 py-3 text-right">Net Cash Flow</th>
            </tr>
          </thead>
          <tbody>
            {[
              "Operating Activities",
              "Investing Activities",
              "Financing Activities",
            ].map((category) => (
              <React.Fragment key={category}>
                {/* Category Header */}
                <tr className="bg-gray-50">
                  <td
                    className="px-4 py-3 font-bold text-lg text-blue-500"
                    colSpan={data.length + 2} // +2 for all columns including totals
                  >
                    {category}
                  </td>
                </tr>

                {/* Accounts within the category */}
                {data[0]?.data &&
                  categorizeAccounts(data[0].data, category)
                    .filter((account) => {
                      // Calculate the total balance for this account across all datasets
                      const totalBalance = data
                        .map((dataset) => {
                          const matchingAccount = dataset.data.find(
                            (acc) => acc.accountName === account.accountName
                          );
                          return matchingAccount ? matchingAccount.netFlow : 0;
                        })
                        .reduce((sum, val) => sum + val, 0);
                      return totalBalance !== 0;
                    })
                    .map((account, accIndex) => (
                      <tr key={accIndex} className="hover:bg-gray-100">
                        <td
                          className="pl-8 py-2 text-gray-700 hover:text-blue-500 hover:underline cursor-pointer"
                          onClick={() => onAccountClick(account)}
                        >
                          {account.accountName}
                        </td>
                        {data.map((dataset, index) => {
                          const matchingAccount = dataset.data.find(
                            (acc) => acc.accountName === account.accountName
                          );
                          return (
                            <td
                              key={index}
                              className="px-4 py-2 text-right text-gray-800"
                            >
                              {matchingAccount
                                ? matchingAccount.netFlow.toLocaleString()
                                : "0"}{" "}
                              Nu.
                            </td>
                          );
                        })}
                        <td className="px-4 py-2 text-right font-semibold">
                          {data
                            .map((dataset) => {
                              const matchingAccount = dataset.data.find(
                                (acc) => acc.accountName === account.accountName
                              );
                              return matchingAccount
                                ? matchingAccount.netFlow
                                : 0;
                            })
                            .reduce((sum, val) => sum + val, 0)
                            .toLocaleString()}{" "}
                          Nu.
                        </td>
                      </tr>
                    ))}

                {/* Totals for the category */}
                <tr className="font-bold border-t border-gray-300 border-dashed">
                  <td className="px-8 py-2">Total {category}</td>
                  {data.map((dataset, index) => {
                    const accounts = categorizeAccounts(dataset.data, category);
                    const { netCashFlow } = calculateTotals(accounts);
                    return (
                      <td key={index} className="px-4 py-2 text-right">
                        {netCashFlow.toLocaleString()} Nu.
                      </td>
                    );
                  })}
                  <td className="px-4 py-2 text-right font-bold text-blue-900">
                    {data
                      .map((dataset) => {
                        const accounts = categorizeAccounts(
                          dataset.data,
                          category
                        );
                        const { netCashFlow } = calculateTotals(accounts);
                        return netCashFlow;
                      })
                      .reduce((sum, val) => sum + val, 0)
                      .toLocaleString()}{" "}
                    Nu.
                  </td>
                </tr>
              </React.Fragment>
            ))}

            {/* Overall Net Cash Flow */}
            <tr className="text-blue-500 font-bold bg-gray-50">
              <td className="px-4 py-2">Net Cash Flow</td>
              {data.map((dataset, index) => {
                const totalNetCashFlow = [
                  "Operating Activities",
                  "Investing Activities",
                  "Financing Activities",
                ]
                  .map((category) => {
                    const accounts = categorizeAccounts(dataset.data, category);
                    const { netCashFlow } = calculateTotals(accounts);
                    return netCashFlow;
                  })
                  .reduce((sum, val) => sum + val, 0);
                return (
                  <td key={index} className="px-4 py-2 text-right">
                    {totalNetCashFlow.toLocaleString()} Nu.
                  </td>
                );
              })}
              <td className="px-4 py-2 text-right font-extrabold">
                {data
                  .map((dataset) => {
                    return [
                      "Operating Activities",
                      "Investing Activities",
                      "Financing Activities",
                    ]
                      .map((category) => {
                        const accounts = categorizeAccounts(
                          dataset.data,
                          category
                        );
                        const { netCashFlow } = calculateTotals(accounts);
                        return netCashFlow;
                      })
                      .reduce((sum, val) => sum + val, 0);
                  })
                  .reduce((sum, val) => sum + val, 0)
                  .toLocaleString()}{" "}
                Nu.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Click outside to close export options */}
      {showExportOptions && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowExportOptions(false)}
        />
      )}
    </div>
  );
};

export default CashFlowTable;